import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import './Lottery.css'
import diamondImg from '../images/diamond_nav@2x.png'
import ghostImg from '../images/B_ghost_3d.png'
import leafImg from '../images/S_fallen_leaf_3d.png'
import lockedImg from '../images/locked_3d.png'

const FALLBACK_SYMBOLS = ['7️⃣', '🍒', '🔔', '💎', '⭐', '🍀', '🎁', '🎰']
const ITEM_HEIGHT = 96

function isImageSymbol(sym) {
  return typeof sym === 'string' && /^https?:\/\//.test(sym)
}

function buildStrip(symbols, finalSymbol, spinCount) {
  const strip = []
  for (let i = 0; i < spinCount; i++) {
    strip.push(symbols[Math.floor(Math.random() * symbols.length)])
  }
  strip.push(finalSymbol)
  return strip
}

function pickLoseSymbols(symbols) {
  let picked
  do {
    picked = [0, 1, 2].map(
      () => symbols[Math.floor(Math.random() * symbols.length)]
    )
  } while (picked[0] === picked[1] && picked[1] === picked[2])
  return picked
}

function LotteryModal({
  title,
  emoji,
  image,
  lines,
  leftLabel,
  rightLabel,
  onLeft,
  onRight,
  onClose,
}) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="modal-body modal-body-center">
          {image ? (
            <img className="modal-emoji-img" src={image} alt="" />
          ) : (
            <div className="modal-emoji">{emoji}</div>
          )}
          <p className="modal-text">
            {lines.map((line, i) => (
              <React.Fragment key={i}>
                {i > 0 && <br />}
                {line}
              </React.Fragment>
            ))}
          </p>
        </div>
        <div className="modal-actions">
          {leftLabel && (
            <button className="modal-cancel-btn" onClick={onLeft}>
              {leftLabel}
            </button>
          )}
          {rightLabel && (
            <button
              className="modal-confirm-btn modal-confirm-btn-wide"
              onClick={onRight}
            >
              {rightLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default function LotteryLayout() {
  const { game_id, ac, se } = useParams()

  const [gameInfo, setGameInfo] = useState(null)

  useEffect(() => {
    if (!game_id || !ac || !se) return
    const host = process.env.REACT_APP_HOST
    axios
      .post(
        host + '/clientWeb/v1/GetGameInfo',
        { game_id },
        {
          headers: {
            'Content-Type': 'text/plain; charset=UTF-8',
            Accept: '*/*',
            ac,
            se,
          },
        }
      )
      .then((res) => {
        setGameInfo(res.data)
      })
      .catch((err) => {
        console.error('GetGameInfo error:', err)
      })
  }, [game_id, ac, se])

  const [isSpinning, setIsSpinning] = useState(false)
  const [result, setResult] = useState(null)
  const [showResultModal, setShowResultModal] = useState(false)
  // 點數餘額不足（JoinGame 03050）／遊玩次數上限（JoinGame 03052）modal
  const [showNoPoints, setShowNoPoints] = useState(false)
  const [showMaxPlay, setShowMaxPlay] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [showOdds, setShowOdds] = useState(false)
  const [showExit, setShowExit] = useState(false)

  const prizeIcons = (gameInfo?.prize_list || [])
    .map((prize) => prize.icon)
    .filter(Boolean)
  const symbols = prizeIcons.length >= 2 ? prizeIcons : FALLBACK_SYMBOLS

  const [reelConfigs, setReelConfigs] = useState([
    { strip: [FALLBACK_SYMBOLS[0]], offset: 0, duration: 0 },
    { strip: [FALLBACK_SYMBOLS[1]], offset: 0, duration: 0 },
    { strip: [FALLBACK_SYMBOLS[2]], offset: 0, duration: 0 },
  ])

  useEffect(() => {
    if (prizeIcons.length < 2) return
    setReelConfigs((prev) =>
      prev.map((config, i) =>
        config.strip.length > 1
          ? config
          : {
              strip: [prizeIcons[i % prizeIcons.length]],
              offset: 0,
              duration: 0,
            }
      )
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameInfo])

  const runSpin = () => {
    if (isSpinning) return

    setIsSpinning(true)

    // 依 prize_list 的 percent 抽獎，剩餘機率視為未中獎
    // reward_type 獎項類型：1=中獎、0=未中獎、-1=扣點
    const usingPrizeIcons = prizeIcons.length >= 2
    let drawn = null
    if (usingPrizeIcons) {
      const roll = Math.random() * 100
      let cumulative = 0
      for (const p of gameInfo.prize_list) {
        if (!p.icon) continue
        cumulative += p.percent || 0
        if (roll < cumulative) {
          drawn = p
          break
        }
      }
    } else if (Math.random() < 0.3) {
      drawn = { reward_type: 1, point: 0 }
    }

    const rewardType = drawn ? drawn.reward_type : 0
    const nextResult = {
      type: rewardType === 1 ? 'win' : rewardType === -1 ? 'deduct' : 'lose',
      prize: drawn,
    }
    setResult(nextResult)

    // 中獎與扣點停在三顆相同圖示，未中獎停在不同圖示
    const matchSymbol =
      drawn && drawn.icon
        ? drawn.icon
        : symbols[Math.floor(Math.random() * symbols.length)]
    const finalSymbols =
      nextResult.type === 'lose'
        ? pickLoseSymbols(symbols)
        : [matchSymbol, matchSymbol, matchSymbol]

    const spinCounts = [20, 26, 32]
    const durations = [2.2, 3.0, 3.8]

    const next = finalSymbols.map((sym, i) => {
      const strip = buildStrip(symbols, sym, spinCounts[i])
      return {
        strip,
        offset: -(strip.length - 1) * ITEM_HEIGHT,
        duration: durations[i],
      }
    })

    setReelConfigs(next.map((c) => ({ ...c, offset: 0, duration: 0 })))

    setTimeout(() => {
      setReelConfigs(next)
    }, 30)

    const lastDuration = durations[durations.length - 1]
    setTimeout(() => {
      setIsSpinning(false)
      setShowResultModal(true)
      sendGamePrize(nextResult)
    }, lastDuration * 1000 + 400)
  }

  const handleSpinClick = () => {
    if (isSpinning) return
    setShowConfirm(true)
  }

  // 確認扣點後先加入遊戲，成功才開始轉動
  const handleConfirmSpin = () => {
    setShowConfirm(false)
    axios
      .post(
        process.env.REACT_APP_HOST + '/clientWeb/v1/JoinGame',
        { game_id },
        { headers: { 'Content-Type': 'application/json', ac, se } }
      )
      .then((res) => {
        const code = String(res.data?.code)
        if (code === '02000') {
          runSpin()
          // JoinGame 已扣掉一次遊玩次數，同步遊戲資訊的剩餘次數
          setGameInfo((prev) =>
            prev && typeof prev.remain_play === 'number'
              ? { ...prev, remain_play: Math.max(prev.remain_play - 1, 0) }
              : prev
          )
          // JoinGame 已扣點，通知 Native 刷新 APP 右上角點數
          sendToNative({ action: 'refreshPoints', payload: {} })
        } else if (code === '03050') {
          // 點數餘額不足
          setShowNoPoints(true)
        } else if (code === '03052') {
          // 已達本遊戲遊玩次數限制
          setShowMaxPlay(true)
        } else {
          console.error('JoinGame failed:', res.data)
        }
      })
      .catch((err) => {
        console.error('JoinGame error:', err)
      })
  }

  // 結果確定後回報獎項，未中獎帶空字串與 0 點，扣點帶負數
  const sendGamePrize = (spinResult) => {
    const isLose = spinResult.type === 'lose'
    const prizePoint = Math.abs(spinResult.prize?.point ?? 0)
    axios
      .post(
        process.env.REACT_APP_HOST + '/clientWeb/v1/SendGamePrize',
        {
          game_id,
          prize_name: isLose ? '' : spinResult.prize?.name || '',
          point: isLose
            ? 0
            : spinResult.type === 'deduct'
            ? -prizePoint
            : prizePoint,
        },
        { headers: { 'Content-Type': 'application/json', ac, se } }
      )
      .then((res) => {
        const code = String(res.data?.code)
        if (code === '24008') {
          // 此遊戲獎項不存在（後端獎項設定問題），先用 alert 擋
          alert('請聯絡客服人員')
          console.error('SendGamePrize failed:', res.data)
        } else if (code !== '02000') {
          console.error('SendGamePrize failed:', res.data)
        }
      })
      .catch((err) => {
        console.error('SendGamePrize error:', err)
      })
      .finally(() => {
        // 獎項回報後請 Native 更新會員點數，同步 APP 右上角點數
        sendToNative({ action: 'refreshPoints', payload: {} })
      })
  }

  // 參考 web_native_bridge_example.html：傳送 JSON message 給 Native App
  // 回傳是否有 Native bridge 可接收（false = 一般瀏覽器）
  const sendToNative = (message) => {
    // iOS WKWebView：Native 端以 "ios" 註冊 WKScriptMessageHandler
    if (window.webkit?.messageHandlers?.ios) {
      console.log('[bridge] postMessage to ios:', message)
      window.webkit.messageHandlers.ios.postMessage(message)
      return true
    }

    // Android WebView：Native 端注入 app.android(message)，只收字串
    if (window.app && typeof window.app.android === 'function') {
      console.log('[bridge] app.android:', message)
      window.app.android(JSON.stringify(message))
      return true
    }

    console.log('[bridge] no native handler found, message not sent:', message)
    return false
  }

  // 請 Native App 關閉目前的 WebView 容器
  const handleExit = () => {
    if (sendToNative({ action: 'back', payload: {} })) return

    // 一般瀏覽器 fallback
    if (window.history.length > 1) {
      window.history.back()
    } else {
      window.close()
    }
  }

  // 填問卷賺點去：請 Native App 前往「任務」首頁
  const handleGoTask = () => {
    const url = 'https://ezc-dlink.dsigroup.com.tw/task'
    if (sendToNative({ action: 'openAppRoute', payload: { url } })) return

    // 一般瀏覽器 fallback
    window.location.href = url
  }

  const handlePlayAgain = () => {
    setShowResultModal(false)
    setResult(null)
    setShowConfirm(true)
  }

  const resultModalContent = result
    ? {
        win: {
          title: '恭喜中獎',
          image: diamondImg,
          lines: [
            `恭喜獲得點數 ${result.prize?.point ?? 0} 點！`,
            '太神啦！趁勝追擊賺更多～',
          ],
        },
        lose: {
          title: '銘謝惠顧',
          image: leafImg,
          lines: ['好可惜！沒有中獎', '別氣餒，好運就在下一場！'],
        },
        deduct: {
          title: '嗝！ 好飽～',
          image: ghostImg,
          lines: [
            `貪吃鬼偷咬一口！扣除點數 ${result.prize?.point ?? 0} 點`,
            '再玩一次，把點數贏回來！',
          ],
        },
      }[result.type]
    : null

  const title = gameInfo?.name || '幸運轉盤'
  const description = gameInfo?.description
  const point = gameInfo?.point
  const maxPlay = gameInfo?.max_play
  // remain_play：該會員在本遊戲的剩餘可遊玩次數（max_play 為 0 時不限次數）
  const remainPlay = gameInfo?.remain_play
  const icon = gameInfo?.icon
  const prizeList = gameInfo?.prize_list || []
  const isDisabled = gameInfo?.enable === 0

  return (
    <div className="lottery-page">
      <main className="lottery-main">
        <div className="lottery-hero">
          {icon && <img className="lottery-hero-icon" src={icon} alt="" />}
          <div className="lottery-hero-text">
            <h1 className="hero-title">{title}</h1>
            {description && <p className="hero-desc">{description}</p>}
          </div>
        </div>

        <div className="slot-card">
          <div className="cost-badge-row">
            <div className="cost-badge">遊玩一次扣除 {point ?? 'X'} 點</div>
          </div>

          <div className="reels-grid">
            {reelConfigs.map((config, idx) => (
              <div
                key={idx}
                className={`reel-window${isSpinning ? ' spinning' : ''}`}
              >
                <div
                  className="reel-strip"
                  style={{
                    transform: `translateY(${config.offset}px)`,
                    transition:
                      config.duration > 0
                        ? `transform ${config.duration}s cubic-bezier(0.15, 0.85, 0.35, 1.05)`
                        : 'none',
                  }}
                >
                  {config.strip.map((sym, i) => (
                    <div key={i} className="reel-item">
                      {isImageSymbol(sym) ? (
                        <img className="reel-item-img" src={sym} alt="" />
                      ) : (
                        sym
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="max-play-row">
            {maxPlay === 0
              ? '遊玩次數 無限'
              : `遊玩上限 ${maxPlay ?? 'x'} 次，剩餘 ${remainPlay ?? 'x'} 次`}
          </div>

          <button
            className="spin-btn"
            onClick={handleSpinClick}
            disabled={isSpinning || isDisabled || !gameInfo}
          >
            {isSpinning ? (
              <span className="spin-dots">
                <span />
                <span />
                <span />
              </span>
            ) : (
              '開始轉動'
            )}
          </button>
        </div>

        <div className="secondary-grid">
          <button className="info-btn" onClick={() => setShowOdds(true)}>
            <span className="lottery-info-icon lottery-info-icon-stars">★</span>
            <span className="info-label">中獎機率</span>
          </button>
          <button className="info-btn" onClick={() => setShowExit(true)}>
            <span className="lottery-info-icon lottery-info-icon-exit">⎋</span>
            <span className="info-label">退出遊戲</span>
          </button>
        </div>

      </main>

      {showOdds && (
        <div className="modal-overlay" onClick={() => setShowOdds(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">中獎機率說明</h2>
              <button
                className="modal-close"
                onClick={() => setShowOdds(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              {prizeList.length === 0 && (
                <div className="modal-empty">尚無獎項資料</div>
              )}
              {prizeList.map((prize, i) => (
                <div key={i} className="prize-row">
                  <span className="prize-line">
                    {[0, 1, 2].map((j) =>
                      isImageSymbol(prize.icon) ? (
                        <img
                          key={j}
                          className="prize-line-icon"
                          src={prize.icon}
                          alt=""
                        />
                      ) : (
                        <span key={j}>{prize.icon}</span>
                      )
                    )}
                  </span>
                  <span className="prize-name">{prize.name}</span>
                  <span className="prize-point">
                    {prize.reward_type === -1
                      ? `-${Math.abs(prize.point)}`
                      : prize.point}{' '}
                    點
                  </span>
                  <span className="prize-percent">{prize.percent}%</span>
                </div>
              ))}
              {prizeList.length > 0 && (
                <p className="prize-footnote">剩餘機率將自動視為「未中獎」</p>
              )}
            </div>
            <button
              className="modal-confirm-btn"
              onClick={() => setShowOdds(false)}
            >
              確認
            </button>
          </div>
        </div>
      )}

      {showConfirm && (
        <LotteryModal
          title="系統訊息"
          emoji="❗"
          lines={[`遊玩一次將扣除 ${point ?? 'X'} 點`, '請確認是否繼續']}
          leftLabel="取消"
          rightLabel="確認"
          onLeft={() => setShowConfirm(false)}
          onRight={handleConfirmSpin}
          onClose={() => setShowConfirm(false)}
        />
      )}

      {showNoPoints && (
        <LotteryModal
          title="系統訊息"
          emoji="❗"
          lines={['點數餘額不足！', '趕快去填寫問卷，累積點數吧～']}
          rightLabel="填問卷賺點去"
          onRight={handleGoTask}
          onClose={() => setShowNoPoints(false)}
        />
      )}

      {showMaxPlay && (
        <LotteryModal
          title="系統訊息"
          image={lockedImg}
          lines={['哎呀，遊玩次數已達上限！', '機台已收攤，去別區挖寶吧～']}
          rightLabel="填問卷賺點去"
          onRight={handleGoTask}
          onClose={() => setShowMaxPlay(false)}
        />
      )}

      {showResultModal && resultModalContent && (
        <LotteryModal
          title={resultModalContent.title}
          image={resultModalContent.image}
          lines={resultModalContent.lines}
          leftLabel="退出遊戲"
          rightLabel="再玩一次"
          onLeft={handleExit}
          onRight={handlePlayAgain}
          onClose={() => setShowResultModal(false)}
        />
      )}

      {showExit && (
        <div className="modal-overlay" onClick={() => setShowExit(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">退出遊戲</h2>
              <button
                className="modal-close"
                onClick={() => setShowExit(false)}
              >
                ✕
              </button>
            </div>
            <p className="modal-text modal-text-left">確定要退出遊戲嗎？</p>
            <div className="modal-actions">
              <button
                className="modal-cancel-btn"
                onClick={() => setShowExit(false)}
              >
                取消
              </button>
              <button className="modal-confirm-btn" onClick={handleExit}>
                確認退出
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
